import { VStack, HStack, Text, Skeleton } from '@wegroup/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { mq } from '../../../../../../../common/src/utils/styledComponentsUtils';
import RadioButton from '../../../../../../../flows/src/components/ui/advisory-flow/components/shared-components/RadioButton';
import { useQuote, useTopProduct } from '../../../../../hooks/useQuoteQueries';
import { getOfferForProductAndQuote } from '../../../../../services/offersService';
import {
  getEuroValue,
  getGuaranteeNameForKey,
} from '../../../../../services/productFactoryService';
import OfferSidebarSingleSelectedOfferPrice from './OfferSidebarSingleSelectedOfferPrice';

interface Props {
  id: string;
  riskObject: string;
  isSelected: boolean;
  onClick: () => void;
}

const SingleSelectedSubOffer: React.FC<React.PropsWithChildren<Props>> = ({
  id,
  riskObject,
  isSelected,
  onClick,
  ...otherProps
}) => {
  const { t } = useTranslation();

  const { data: product, isFetching: isProductFetching } = useTopProduct(
    riskObject,
    id,
  );
  const { data: quote, isFetching: isQuoteFetching } = useQuote(riskObject, id);

  if (isProductFetching || isQuoteFetching)
    return (
      <SubOfferContainer {...otherProps}>
        <StyledRadioButton isSelected={isSelected} />
        <VStack align="stretch" spacing="1" width="100%">
          <Skeleton h="3" maxW="44" />
          <Skeleton h="3" maxW="28" />
        </VStack>
      </SubOfferContainer>
    );

  if (!quote || !product)
    return (
      <SubOfferContainer {...otherProps}>
        <StyledRadioButton isSelected={isSelected} />
        <VStack align="flex-start" spacing={0}>
          <Text
            onClick={onClick}
            cursor="pointer"
            color="text.gray"
            flexGrow={1}
          >
            {product
              ? `${product.info.company?.name} ${getGuaranteeNameForKey(
                  product.info.insurance_name,
                  product.guarantee_content,
                )}`
              : 'Unknown offer'}
          </Text>
          <HStack>
            <OfferSidebarSingleSelectedOfferPrice
              price="-"
              interval="-"
              fontSize="md"
            />
          </HStack>
        </VStack>
      </SubOfferContainer>
    );

  const offer = getOfferForProductAndQuote(product, quote);

  return (
    <SubOfferContainer {...otherProps}>
      <StyledRadioButton onClick={onClick} isSelected={isSelected} />
      <VStack align="flex-start" spacing={0}>
        <Text onClick={onClick} cursor="pointer" color="text.gray" flexGrow={1}>
          {offer.info.company?.name}{' '}
          {getGuaranteeNameForKey(
            offer.info.insurance_name,
            offer.guarantee_content,
          )}
        </Text>
        <HStack>
          <OfferSidebarSingleSelectedOfferPrice
            price={
              (getEuroValue(
                offer.state,
                offer.premium?.total_premium,
              ) as string) || '-'
            }
            interval={
              offer.details?.payment_interval
                ? t(`spread.${offer.details.payment_interval.toUpperCase()}`)
                : '-'
            }
            fontSize="md"
          />
        </HStack>
      </VStack>
    </SubOfferContainer>
  );
};

const SubOfferContainer: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
  ...otherProps
}) => {
  return (
    <HStack spacing={3} fontSize="sm" width="100%" minH="10" {...otherProps}>
      {children}
    </HStack>
  );
};

const StyledRadioButton = styled(RadioButton)`
  min-width: 2.4rem;
  height: 2.3rem;
  margin: 0 0.7rem;
  padding: 2px;

  ${mq.laptop} {
    min-width: 1.6rem;
    height: 1.6rem;
  }
`;

export default SingleSelectedSubOffer;
